import { FC, useEffect, useState } from "react";
import SubmitItem from "../../../components/template/SubmitItem/SubmitItem";
import { FieldValues, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useLocation, Form } from "react-router-dom";
import { Toast } from "../../../components/atom/Toast/Toast";
import {
  EDIT_USER_URL,
  RESET_PASSWORD,
  USER_URL,
} from "../../../utils/endpoints";
import { handleSubmitItem } from "../../../helpers/handleSubmitItem";
import { handleDeleteItem } from "../../../helpers/handleDeleteItem";
import { UsersItemSchema } from "../data/UsersItemSchema";
import { handleResetPassword } from "../../../helpers/handleResetPassword";
import { handleUserStateChange } from "../../../helpers/handleUserStateChange";
import UserSubmitForm from "../forms/UserSubmitForm";
import { useAuth } from "../../../provider/AuthProvider";
import { AlertDialogConfig } from "../../../types/Components/Alert/AlertDialogConfig";
import api from "../../../utils/api";
import { clearUsersCache } from "../../../helpers/checkEmailExists";

const EditUser: FC = () => {
  const { addToast } = Toast();
  const navigate = useNavigate();
  const location = useLocation();
  const { edit_user } = location.state || {};
  const [idItem] = useState<number>(edit_user?.id || 0);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState<string>(edit_user?.name || "");
  const [isLoaded, setIsLoaded] = useState(true);

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    resetField,
    setValue,
  } = useForm({
    resolver: zodResolver(UsersItemSchema),
    mode: "onChange",
    shouldUseNativeValidation: false,
  });

  const { auth } = useAuth();
  const user = auth.user;

  useEffect(() => {
    if (edit_user) {
      setValue("name", edit_user.name);
      setValue("position", edit_user.position ? edit_user.position : "");
      setValue("email", edit_user.email);
      setValue("allowReceiveAlerts", edit_user.allowReceiveAlerts);
      setValue("phone", edit_user.phoneNumber ? edit_user.phoneNumber : "");
      setValue("roleOption", edit_user.roles[0]);
      setValue(
        "provinceOption",
        edit_user.userProvince ? edit_user.userProvince : undefined,
      );
      setValue(
        "countyOption",
        edit_user.userCounty ? edit_user.userCounty : undefined,
      );
      setValue(
        "organizationOption",
        edit_user.organization ? edit_user.organization : undefined,
      );
      setValue(
        "partnerOption",
        edit_user.partner ? edit_user.partner : undefined,
      );
      setValue("isActive", edit_user.isActive);
      setValue("currentEmail", edit_user.email);
      setIsFormInitialized(true);

      setTitle(edit_user.name);
    }
  }, [edit_user, setValue]);

  const onSubmit = async (data: FieldValues) => {
    // TODO:  Pretende receber Alertas do SISAN também por telemóvel? (data.allowReceiveAlerts)
    const bodyRequest = {
      id: idItem,
      name: data.name,
      email: data.email ? data.email : null,
      position: data.position ? data.position : "",
      organizationId:
        data.organizationOption && !data.partnerOption
          ? data.organizationOption.id
          : null,
      phoneNumber: data.phone ? data.phone : "",
      allowReceiveAlerts: false,
      countyId: data.countyOption ? data.countyOption.id : null,
      provinceId: data.provinceOption ? data.provinceOption.id : null,
      partnerId:
        data.partnerOption && !data.organizationOption
          ? data.partnerOption.id
          : null,
      roles: data.roleOption ? [data.roleOption.id] : null,
    };

    await handleSubmitItem(
      () => api.put(EDIT_USER_URL, bodyRequest),
      addToast,
      setIsLoaded,
    );
    navigate("/utilizadores");
  };

  useEffect(() => {
    return () => {
      clearUsersCache();
    };
  }, []);

  const handleDelete = () => {
    handleDeleteItem(USER_URL, setIsAlertOpen, idItem, addToast, setIsLoaded);
  };

  const handlePasswordReset = async () => {
    await handleResetPassword(
      () => api.patch(RESET_PASSWORD, { email: edit_user.email }),
      addToast,
    );
  };

  const handleStateChange = async (isActive: boolean) => {
    await handleUserStateChange(idItem, isActive, addToast);
  };

  const alertDialogConfig: AlertDialogConfig = {
    setIsAlertOpen,
    isAlertOpen,
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <SubmitItem
        title={title}
        handleSubmitItem={handleSubmit(onSubmit)}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        handleDeleteItem={
          user.permissions.includes("DeleteUser") ? handleDelete : undefined
        }
        deleteButtonName="Eliminar utilizador"
        navigatePath="/utilizadores"
        alertDialogConfig={alertDialogConfig}
        isLoaded={isLoaded}
        handleStateChange={handleStateChange}
        initialIsActive={edit_user && edit_user.isActive}
      >
        <UserSubmitForm
          errors={errors}
          control={control}
          resetField={resetField}
          watch={watch}
          isFormInitialized={isFormInitialized}
          isDisabled={!isEditing}
          handlePasswordReset={
            user.role.name === "SuperAdmin" &&
            user.permissions.includes("ResetPassword") &&
            handlePasswordReset
          }
        />
      </SubmitItem>
    </Form>
  );
};

export default EditUser;
